import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Field,
  Input,
  Radio,
  RadioGroup,
  Tag,
  TagGroup,
  Text,
} from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
} from "@onetasklist/shared";
import {
  useSlackSourceSettingsText,
  useSlackSourceSettingsViewModel,
  type SlackImportMode,
} from "@onetasklist/shared/sources/slack";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface SlackSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const SlackSourceSettings: React.FC<SlackSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useSlackSourceSettingsText(t);
  const vm = useSlackSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [channelsQuery, setChannelsQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.slack.sectionChannels",
      )}
    >
      <Field orientation="horizontal" label={text.modeLabelText}>
        <RadioGroup
          value={vm.mode}
          onChange={(_, data) => vm.onSelectMode(data.value as SlackImportMode)}
        >
          <Radio value="reactions" label={text.modeReactionsText} />
          <Radio value="lists" label={text.modeListsText} />
        </RadioGroup>
      </Field>

      {vm.isReactionMode && (
        <>
          <Field
            orientation="horizontal"
            label={text.reactionLabelText}
            hint={text.reactionHintText}
            validationState={vm.hasNoReactions ? "error" : "none"}
            validationMessage={
              vm.hasNoReactions ? text.reactionRequiredText : undefined
            }
          >
            <SourceMultiTagPicker
              selectedIds={vm.reactionNames}
              query={vm.reactionQuery}
              placeholder={text.reactionPlaceholderText}
              options={vm.reactionOptions.map((name) => ({
                id: name,
                label: name,
              }))}
              onQueryChange={vm.onReactionQueryChange}
              onSelectionChange={vm.onSelectReactions}
              minQueryLength={0}
              typeToSearchText={text.reactionHintText}
              isLoading={!vm.reactionsLoaded}
            />
          </Field>

          <Field orientation="horizontal" label={text.includeFilesText}>
            <Checkbox
              checked={vm.includeFiles}
              onChange={(_, data) =>
                vm.onToggleIncludeFiles(Boolean(data.checked))
              }
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.channelLabelText}
            hint={text.channelHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedChannelIds}
              query={channelsQuery}
              placeholder={text.channelPlaceholderText}
              options={vm.channels.map((channel) => ({
                id: channel.id,
                label: channel.name ?? channel.id,
              }))}
              onQueryChange={setChannelsQuery}
              onSelectionChange={vm.onSelectChannels}
              minQueryLength={0}
              typeToSearchText={text.channelHintText}
              isLoading={!vm.channelsLoaded}
            />
          </Field>

          {!vm.channelsLoaded && <Text>{text.loadingChannelsText}</Text>}
          {vm.hasError && <Text>{text.loadErrorText}</Text>}
        </>
      )}

      {vm.isListMode && (
        <>
          <Field
            orientation="horizontal"
            label={text.listLabelText}
            hint={text.listHintText}
            validationState={vm.hasListError ? "error" : "none"}
            validationMessage={vm.hasListError ? text.listErrorText : undefined}
          >
            <div className="connections-page__slack-lists">
              {vm.lists.length === 0 ? (
                <Text>{text.noListsText}</Text>
              ) : (
                <TagGroup onDismiss={(_, data) => vm.onRemoveList(data.value)}>
                  {vm.lists.map((list) => (
                    <Tag
                      key={list.id}
                      value={list.id}
                      dismissible
                      dismissIcon={{ "aria-label": text.removeListText }}
                    >
                      {list.title ?? list.id}
                    </Tag>
                  ))}
                </TagGroup>
              )}
              <div className="connections-page__slack-list-add">
                <Input
                  value={vm.listReference}
                  placeholder={text.listPlaceholderText}
                  onChange={(_, data) => vm.onChangeListReference(data.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && vm.canAddList) {
                      event.preventDefault();
                      void vm.onAddList();
                    }
                  }}
                />
                <Button
                  disabled={!vm.canAddList}
                  onClick={() => void vm.onAddList()}
                >
                  {vm.isResolvingList
                    ? text.resolvingListText
                    : text.addListText}
                </Button>
              </div>
            </div>
          </Field>

          <Field orientation="horizontal" label={text.onlyAssignedToMeText}>
            <Checkbox
              checked={vm.onlyAssignedToMe}
              onChange={(_, data) =>
                vm.onToggleOnlyAssignedToMe(Boolean(data.checked))
              }
            />
          </Field>
        </>
      )}
    </SettingsSectionCard>
  );
};
